// Client for the Django microservice's WebRTC signaling "rooms" service.
//
// These endpoints are intentionally TOKENLESS — the room UUID is the only
// secret. Anyone with the link can join. That makes the service reusable
// for any future P2P feature (not just the interview-record flow), and
// keeps the wire format simple: no auth headers, no JWT refresh dance.
//
// REST surface (verified live 2026-06-24):
//   POST /api/v1/rooms/            → create
//   GET  /api/v1/rooms/{id}/       → fetch state
//   POST /api/v1/rooms/{id}/close/ → mark closed
//   GET  /api/v1/ice-servers/      → ICE config (STUN-only for now;
//                                    will gain TURN credentials here
//                                    when/if we accept that bill —
//                                    FE code does NOT change)
//
// WebSocket:
//   wss://.../ws/rooms/{id}/?role=host|guest
//   Server emits {type:'peer-joined', role} when the 2nd peer arrives
//   and stamps relayed messages with {from:'host'|'guest'}.

import AXIOS from 'axios';

// Same host as the existing uploaderApi.js — kept as its own constant so
// this module doesn't reach into the uploader client (whose axios instance
// is JWT-gated and would 401 these tokenless calls).
const ROOMS_HOST = 'https://uploader.projector.app';
const REST_BASE = `${ROOMS_HOST}/api/v1`;
// Derive the wss:// host by swapping the scheme on the same origin so we
// never drift if the host ever changes.
const WS_BASE = `${ROOMS_HOST.replace(/^http/, 'ws')}/ws/rooms`;

const roomsAxios = AXIOS.create({
  baseURL: REST_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

export const createRoom = async (metadata) => {
  const res = await roomsAxios.post('/rooms/', metadata ? { metadata } : {});
  return res.data; // { id, expires_at, ice_servers }
};

export const getRoom = async (roomId) => {
  const res = await roomsAxios.get(`/rooms/${roomId}/`);
  return res.data; // { id, status, created_at, expires_at, host_connected, guest_connected, is_full, metadata }
};

export const closeRoom = async (roomId) => {
  const res = await roomsAxios.post(`/rooms/${roomId}/close/`);
  return res.data;
};

export const getIceServers = async () => {
  const res = await roomsAxios.get('/ice-servers/');
  // Returns { iceServers: [...] }; pass straight into RTCPeerConnection.
  return res.data;
};

// Build the signaling socket URL for a given room + role. The role MUST be
// 'host' or 'guest' — anything else is rejected by the consumer.
export const getRoomSignalingUrl = (roomId, role) => {
  if (!roomId) throw new Error('getRoomSignalingUrl: roomId is required');
  if (role !== 'host' && role !== 'guest') {
    throw new Error(`getRoomSignalingUrl: invalid role "${role}"`);
  }
  return `${WS_BASE}/${roomId}/?role=${role}`;
};

// Exposed for the few callers that need the raw host (e.g. building the
// guest-shareable link). Don't reach into REST_BASE / WS_BASE directly.
export const roomsHost = ROOMS_HOST;
